import { motion } from 'framer-motion';
import { 
  Monitor, 
  ChefHat, 
  BarChart3, 
  Truck, 
  Package, 
  Users,
  ArrowRight,
  ChevronDown,
  Sparkles
} from 'lucide-react';

const modules = [
  {
    icon: Monitor,
    title: 'PDV com IA',
    description: 'Frente de caixa inteligente que sugere combos, identifica padrões de compra e otimiza o atendimento.',
    color: 'bg-blue-500',
    aiFeature: 'Sugestões inteligentes de venda',
    features: ['Recomendação de combos por IA', 'Análise de padrão de consumo', 'Previsão de demanda']
  },
  {
    icon: ChefHat,
    title: 'KDS Inteligente',
    description: 'Cozinha digital com priorização automática por IA, tempo de preparo otimizado e gestão preditiva.',
    color: 'bg-orange-500',
    aiFeature: 'Priorização automática por IA',
    features: ['Sequenciamento inteligente', 'Alertas preditivos', 'Otimização de tempo']
  },
  {
    icon: Package,
    title: 'Estoque Preditivo',
    description: 'IA que prevê consumo, evita desperdício e automatiza pedidos de reposição antes de acabar.',
    color: 'bg-emerald-500',
    aiFeature: 'Previsão de consumo',
    features: ['Compra automática inteligente', 'Redução de 30% desperdício', 'Alertas antecipados']
  },
  {
    icon: Truck,
    title: 'Suprimentos Smart',
    description: 'Gestão de fornecedores com IA que negocia preços, compara cotações e otimiza entregas.',
    color: 'bg-cyan-500',
    aiFeature: 'Negociação automatizada',
    features: ['Comparador de preços IA', 'Roteirização inteligente', 'Pedidos automáticos']
  },
  {
    icon: Users,
    title: 'Mapeamento de Clientes',
    description: 'IA que analisa comportamento, segmenta clientes e personaliza ofertas para fidelização.',
    color: 'bg-violet-500',
    aiFeature: 'Análise comportamental',
    features: ['Segmentação automática', 'Ofertas personalizadas', 'Previsão de churn']
  },
  {
    icon: BarChart3,
    title: 'Analytics Avançado',
    description: 'Dashboard com insights de IA, relatórios preditivos e recomendações de ação em tempo real.',
    color: 'bg-indigo-500',
    aiFeature: 'Insights automáticos',
    features: ['Previsão de faturamento', 'Tendências de mercado', 'Alertas de oportunidade']
  }
];

export default function Features() {
  return (
    <section id="modulos" className="py-16 md:py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container-custom">
        {/* Header */}
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-100 to-blue-100 text-violet-700 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Módulos com Inteligência Artificial
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Tecnologia de
            <span className="bg-gradient-to-r from-violet-600 to-blue-600 bg-clip-text text-transparent"> IA</span> em cada módulo
          </h2>
          <p className="text-lg text-slate-600">
            Automação inteligente que aprende com seu negócio, reduz custos operacionais 
            e aumenta seu faturamento de forma autônoma.
          </p>
        </motion.div>

        {/* Modules Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {modules.map((module, index) => (
            <motion.div
              key={index}
              className="group bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-xl hover:border-violet-200 transition-all duration-300 relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              {/* AI Badge */}
              <div className="absolute top-4 right-4">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-violet-100 to-blue-100 rounded-full text-[10px] font-semibold text-violet-700">
                  <span>🤖</span> IA
                </span>
              </div>

              {/* Icon */}
              <div className={`w-14 h-14 ${module.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                <module.icon className="w-7 h-7 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                {module.title}
              </h3>
              
              {/* AI Feature highlight */}
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-violet-50 rounded-lg text-xs font-medium text-violet-700 mb-3">
                <Sparkles className="w-3 h-3" />
                {module.aiFeature}
              </div>

              <p className="text-slate-600 mb-5 leading-relaxed">
                {module.description}
              </p>

              {/* Features */}
              <ul className="space-y-2 mb-6">
                {module.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="w-1.5 h-1.5 bg-violet-500 rounded-full" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Link */}
              <a 
                href="#contato" 
                className="inline-flex items-center gap-2 text-violet-600 font-medium text-sm group-hover:gap-3 transition-all"
              >
                Saiba mais
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p className="text-slate-600 mb-4">
            Todos os módulos integrados por um único valor
          </p>
          <a 
            href="https://wa.me/5511934664558?text=Olá! Gostaria de saber os planos e preços do AVAK." 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/25"
          >
            Ver planos e preços
            <ArrowRight className="w-5 h-5" />
          </a>
        </motion.div>

        {/* Indicador de mais conteúdo */}
        <motion.div 
          className="flex justify-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <a href="#depoimentos" className="flex flex-col items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors">
            <span className="text-sm font-medium">Veja depoimentos</span>
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
